import { useQuery } from "@tanstack/react-query";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MAX_USER_PAGES, MAX_USERS_LOADED, RESULTS_LIMIT_USER } from "@/constants/resultsLimits";
import createUserGroupFilter from "@/utilities/createUserGroupFilter";
import { fetchAllSortedById } from "@/utilities/query/fetchAllSortedById";
import { isRealDriverId } from "@/utilities/user/isRealDriverId";

/**
 * The User fields consumers read: `id` (paging, lookups), `name`/`firstName`/`lastName`
 * (`getUserDisplayName`, e-mail fallback), `activeFrom`/`activeTo` (active-driver filter) and
 * `keys` (tachograph card number). A field missing here comes back undefined.
 */
const DRIVER_FIELDS = ["id", "name", "firstName", "lastName", "activeFrom", "activeTo", "keys", "isDriver"];

/**
 * Every driver in the database, subject to the group filter.
 *
 * For listing drivers, scoped to the page's groups. To name the driver an entity references, use
 * `useDriverNamesByIds` / `useUsersByUserIds`: the referenced driver's own groups need not overlap
 * the picked ones, and a scoped list would leave the name blank.
 *
 * Read with `Get`, not `GetFeed`: Geotab does not support `GetFeed` for `User` and rejects it, which
 * also fails every other call batched with it. `User` is capped at {@link RESULTS_LIMIT_USER} per
 * request, so the drivers are paged sorted by id ({@link fetchAllSortedById}), at most
 * {@link MAX_USER_PAGES} pages. `isTruncated` is true when that bound was hit and more drivers may
 * exist than were returned. The caller decides how to surface that.
 *
 * Drivers are selected with `userSearchType: "Driver"` (UserSearch.userSearchType), the documented
 * way to ask for drivers only.
 *
 * Archived drivers are included. A trip from three weeks ago belongs to whoever drove it, whether
 * or not they still work here.
 */
export function useDrivers(enabled = true) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();

  const query = useQuery({
    queryKey: [
      "drivers",
      activeSessionId,
      groups?.map(group => group.id),
      groupFilterCondition,
    ] as const,

    queryFn: ({ signal }) =>
      fetchAllSortedById(
        geotabClient,
        "User",
        createUserGroupFilter(groups, groupFilterCondition, { userSearchType: "Driver" }),
        {
          pageSize: RESULTS_LIMIT_USER,
          maxPages: MAX_USER_PAGES,
          propertySelector: { fields: DRIVER_FIELDS, isIncluded: true },
          signal,
        },
      ),
    enabled,
    // A retry restarts paging at page 1; three retries of a multi-page read are too many sequential Gets.
    retry: 1,
  });

  // Object.assign keeps TanStack's tracked result: a spread would read every field and re-render
  // the caller on each fetch-state change.
  return Object.assign(query, { isTruncated: (query.data?.length ?? 0) >= MAX_USERS_LOADED });
}

export function driverLabel(
  driverId: string | null,
  names: Map<string, string>,
  unknownLabel: (id: string) => string,
): string {
  if (driverId === null) return "";
  const name = names.get(driverId);
  return name ?? unknownLabel(driverId);
}

/**
 * The id of the driver a referencing entity names, or `null` when it names none.
 *
 * @param reference - The driver as it came off the entity, populated with an id at most.
 */
export function driverIdOf(reference: { id?: string } | undefined | null): string | null {
  const id = reference?.id;
  return isRealDriverId(id) ? id : null;
}
